import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (r) => r.toLowerCase().replace(/[^a-z]+/g, "-");

export default function AdminCrewSplit({ lead, token, onSaved }) {
  const [open, setOpen] = useState(false);
  const [split, setSplit] = useState({});
  const [saving, setSaving] = useState(false);
  const roles =
    lead.roles_needed && lead.roles_needed.length ? lead.roles_needed : ["Ushers"];

  useEffect(() => {
    if (open) {
      const existing = lead.crew_split || {};
      setSplit(
        Object.fromEntries(
          roles.map((r) => [r, existing[r] != null ? String(existing[r]) : ""])
        )
      );
    }
  }, [open]);

  const total = Object.values(split).reduce((s, v) => s + (parseInt(v, 10) || 0), 0);

  const save = async () => {
    const clean = {};
    for (const r of roles) {
      const v = parseInt(split[r], 10);
      if (!Number.isNaN(v) && v > 0) clean[r] = v;
    }
    setSaving(true);
    try {
      await axios.put(
        `${API}/leads/${lead.id}/crew-split`,
        { crew_split: clean },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      onSaved(clean);
      toast.success("Crew split saved — quote PDFs now use exact numbers.");
      setOpen(false);
    } catch {
      toast.error("Could not save crew split.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        data-testid={`admin-crew-split-${lead.id}`}
        onClick={() => setOpen(true)}
        className="p-2 text-slate-500 hover:text-sky-400 transition-colors"
        aria-label="Set crew split"
        title="Set exact crew per role"
      >
        <Users className="w-4 h-4" />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131622] border-white/10 text-white sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-bold uppercase">
              Crew <span className="text-sky-500">Split</span>
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Exact crew numbers per role for {lead.contact_name}'s quote.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 mt-1">
            {roles.map((r) => (
              <div key={r} className="flex items-center justify-between gap-3">
                <label className="text-sm text-slate-300">{r}</label>
                <Input
                  data-testid={`crew-split-input-${slug(r)}`}
                  type="number"
                  min="0"
                  value={split[r] ?? ""}
                  onChange={(e) =>
                    setSplit((s) => ({ ...s, [r]: e.target.value }))
                  }
                  className="w-24 bg-[#0F111A] border-white/10 text-white rounded-sm focus-visible:ring-sky-500"
                />
              </div>
            ))}
            <div className="flex items-center justify-between border-t border-white/10 pt-3">
              <span className="text-sm font-semibold text-white">Total crew</span>
              <span
                data-testid="crew-split-total"
                className="font-mono-tactical text-xl font-bold text-sky-500"
              >
                {total}
              </span>
            </div>
            <button
              data-testid="crew-split-save-button"
              onClick={save}
              disabled={saving}
              className="w-full inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-[#090A0F] font-bold rounded-sm px-6 py-2.5 transition-colors duration-200"
            >
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              Save Crew Split
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
